// src/pages/AdminPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/adminpanel.css';

const ADMIN_API = `${process.env.REACT_APP_API_URL}/api/admin`;

// ฟังก์ชันช่วยสุ่มอักขระ [a–z0–9]
function randomString(len) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < len; i++) {
    s += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return s;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().slice(0,10)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const timerRef = useRef(null);
  const clockRef = useRef(null);

  // Update current time every second สำหรับคำนวณเวลาคงเหลือ
  useEffect(() => {
    clockRef.current = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      if (clockRef.current) clearInterval(clockRef.current);
    };
  }, []);

  // โหลดประวัติการ Generate
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      navigate('/admin/login', { replace: true });
      return;
    }

    async function fetchHistory() {
      try {
        const res = await fetch(`${ADMIN_API}/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login', { replace: true });
          return;
        }
        
        if (!res.ok) {
          throw new Error(`Fetch history failed: ${res.status}`);
        }
        
        const data = await res.json();
        setHistory(data);
        setCount(data.length);
        setIsLoading(false);
      } catch (err) {
        setError('Fetch history failed');
        console.error(err);
        setIsLoading(false);
      }
    }
    
    fetchHistory();
  }, [navigate]);

  // ฟังก์ชัน Generate
  const handleGenerate = async () => {
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      navigate('/admin/login', { replace: true });
      return;
    }

    const username = randomString(8);
    const password = randomString(12);
    setError('');

    try {
      const res = await fetch(`${ADMIN_API}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ username, password })
      });

      if (res.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login', { replace: true });
        return;
      }
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.msg || `Generate failed ${res.status}`);
      }

      const data = await res.json();
      
      setGenerated({
        username: data.username,
        password: password,
        generatedAt: data.generatedAt,
        expiresAt: data.expiresAt
      });
      
      setCount(c => c + 1);
      setHistory(h => [
        {
          _id: data.username + data.generatedAt,
          username: data.username,
          generatedAt: data.generatedAt,
          expiresAt: data.expiresAt
        },
        ...h
      ]);

      // เริ่มนับถอยหลังสำหรับ generated user ล่าสุด
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const expTs = new Date(data.expiresAt).getTime();
        const secs = Math.max(0, Math.floor((expTs - now) / 1000));
        setRemaining(secs);
        if (secs <= 0) {
          clearInterval(timerRef.current);
        }
      }, 1000);
    } catch (err) {
      setError(`Generate user error: ${err.message}`);
      console.error('Generate user error:', err);
    }
  };

  // เคลียร์ interval ตอน unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // แปลงวินาทีเป็น mm:ss หรือ hh:mm:ss
  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Expired';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // คำนวณเวลาคงเหลือสำหรับแต่ละ user
  const calculateTimeLeft = (expiresAt) => {
    const expTime = new Date(expiresAt).getTime();
    const timeLeft = Math.max(0, Math.floor((expTime - currentTime) / 1000));
    return formatTime(timeLeft);
  };

  // ตรวจสอบสถานะของ user
  const getUserStatus = (expiresAt) => {
    const expTime = new Date(expiresAt).getTime();
    const timeLeft = expTime - currentTime;
    
    if (timeLeft <= 0) return 'expired';
    if (timeLeft <= 300000) return 'expiring'; // น้อยกว่า 5 นาที
    return 'active';
  };

  // คำนวณ summary รายวัน
  const dailyCount = history.filter(h => {
    const day = new Date(h.generatedAt).toISOString().slice(0,10);
    return day === selectedDate;
  }).length;

  // ค่า min date (ย้อนหลังได้สูงสุด 30 วัน)
  const minDate = new Date(Date.now() - 30*24*60*60*1000)
    .toISOString().slice(0,10);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login', { replace: true });
  };

  // แสดง loading state
  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-panel">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-panel">
        <div className="admin-header">
          <h2>Admin Panel – Generate User</h2>
          <button
            className="btn-logout"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        <button className="btn-generate" onClick={handleGenerate}>
          Generate User
        </button>

        <p>Generated count: <strong>{count}</strong></p>

        {/* แสดง username/password + นับถอยหลัง */}
        {generated && (
          <div className="generated-info">
            <p>Username: <code>{generated.username}</code></p>
            <p>Password: <code>{generated.password}</code></p>
            <p>
              Time left:{' '}
              <strong>{formatTime(remaining)}</strong>
            </p>
          </div>
        )}

        {/* Daily Summary */}
        <div className="daily-summary">
          <h3>Daily Summary</h3>
          <label>
            Select date:{' '}
            <input
              type="date"
              value={selectedDate}
              min={minDate}
              max={new Date().toISOString().slice(0,10)}
              onChange={e => setSelectedDate(e.target.value)}
            />
          </label>
          <p>Generated today: <strong>{dailyCount}</strong> users</p>
        </div>

        {/* ตารางประวัติ */}
        {history.length > 0 && (
          <>
            <h3>History</h3>
            <div className="table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Generated At</th>
                    <th>Expires At</th>
                    <th>Time Left</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const status = getUserStatus(h.expiresAt);
                    return (
                      <tr key={h._id} className={`status-${status}`}>
                        <td>{h.username}</td>
                        <td>{new Date(h.generatedAt).toLocaleString('th-TH', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</td>
                        <td>{new Date(h.expiresAt).toLocaleString('th-TH', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</td>
                        <td className={`time-left ${status}`}>
                          {calculateTimeLeft(h.expiresAt)}
                        </td>
                        <td>
                          <span className={`status-badge ${status}`}>
                            {status === 'expired' ? 'Expired' : 
                             status === 'expiring' ? 'Nearly expired' : 
                             'Active'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}